import { useCallback, useState } from 'react';
import { sendSupportForm } from '../../lib/use-cases/send-support-form';
import { logEvent } from '@amplitude/analytics-browser';
import { EVENT_TYPES } from '../../lib/shared/event-types';
import { useUser } from './use-user';
import { useRouting } from './use-routing';
import { buildSupportReason } from './build-support-reason';

const MIN_MESSAGE_LENGTH = 1;

function useSupportForm() {
    const { userData } = useUser();
    const { lang } = useRouting();
    const [isPending, setIsPending] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [isFail, setIsFail] = useState(false);
    const [fields, setFields] = useState({ reason: '', message: '' });
    const [errors, setErrors] = useState({ reason: '', message: '', form: '' });

    const changeField = useCallback((name, value) => {
        if (errors[name] || errors.form) {
            setErrors(prevState => ({ ...prevState, [name]: '', form: '' }));
        }

        setFields(prevState => ({ ...prevState, [name]: value }));
    }, [errors]);

    const submitForm = async (e) => {
        e.preventDefault();

        const initialErrors = {};

        try {
            setIsPending(true);

            if (!fields.reason) {
                initialErrors.reason = 'Please select an option to continue';
            }

            if (!fields.message.trim()) {
                initialErrors.message = 'The message field can\'t be empty. Please write your message before sending';
            } else if (fields.message.trim().length < MIN_MESSAGE_LENGTH) {
                initialErrors.message = `Your message is too short. Please enter at least ${MIN_MESSAGE_LENGTH} ${MIN_MESSAGE_LENGTH === 1 ? 'character' : 'characters'} before sending`;
            }

            if (Object.keys(initialErrors).length > 0) {
                return setErrors(prevState => ({ ...prevState, ...initialErrors }));
            }

            logEvent(EVENT_TYPES.SUPPORT_SEND_CLICKED, { reason: fields.reason, 'has_message': Boolean(fields.message) });

            await sendSupportForm({ email: userData.email, reason: buildSupportReason(fields.reason, lang), ...(fields.message ? { message: fields.message } : {}) });

            setIsSuccessful(true);
        } catch {
            setIsFail(true);
            logEvent(EVENT_TYPES.SUPPORT_ERROR_VIEWED);
        } finally {
            setIsPending(false);
        }
    };

    const resetForm = () => {
        setIsFail(false);
        setErrors({ reason: '', message: '', form: '' });
        setFields({ reason: '', message: '' });
    };

    return {
        fields,
        errors,
        isPending,
        isSuccessful,
        isFail,
        submitForm,
        resetForm,
        changeField
    };
}

export {
    useSupportForm
};
